<!-- src/components/forms/completed/RightToWorkCompleted.vue -->
<template>
  <div class="space-y-12 animate-fadeIn">
    
    <!-- 1. Identity & Citizenship Status -->
    <section>
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div class="flex items-center gap-3">
          <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
            <IdentificationIcon class="h-5 w-5 text-[#0A1630]" />
          </div>
          <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">1. Eligibility Status</h3>
        </div>

        <!-- External Verification Link for Admins -->
        <a 
          href="https://right-to-work.service.gov.uk/rtw-view/checker-details" 
          target="_blank" 
          class="inline-flex items-center gap-2 px-4 py-2 bg-[#0A1630] text-[#20FCF1] rounded-xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all shadow-lg no-print"
        >
          <ArrowTopRightOnSquareIcon class="h-4 w-4" />
          Verify on GOV.UK
        </a>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50 p-8 rounded-[40px] border border-slate-100 shadow-inner">
        <div class="space-y-6">
          <div class="StatCard bg-white border-l-8 border-l-[#0A1630]">
            <span class="label text-[#0A1630]">Citizenship Type</span>
            <span class="value uppercase">{{ formatText(submissionData.citizenshipType) }}</span>
          </div>

          <!-- Enhanced Share Code Card with Copy Logic -->
          <div v-if="submissionData.shareCode" class="StatCard bg-[#0A1630] text-white relative group">
            <span class="label text-[#20FCF1]">Home Office Share Code</span>
            <div class="flex items-center justify-between mt-1">
              <span class="value font-mono tracking-[0.3em] text-[#20FCF1]">{{ submissionData.shareCode }}</span>
              <button 
                @click="copyToClipboard(submissionData.shareCode)" 
                class="p-2 bg-white/10 rounded-lg hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all no-print"
                title="Copy Share Code"
              >
                <DocumentDuplicateIcon class="h-4 w-4" />
              </button>
            </div>
            <p v-if="copyFeedback" class="absolute -bottom-6 right-0 text-[9px] font-black text-[#20FCF1] uppercase animate-bounce">
              {{ copyFeedback }}
            </p>
          </div>
        </div>

        <div class="space-y-4">
          <p class="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-2">Verified Personnel Data</p>
          <div class="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
            <div>
              <p class="text-sm font-black text-[#0A1630] uppercase">{{ userProfile?.firstName || submissionData.firstName }} {{ userProfile?.lastName || submissionData.lastName }}</p>
              <p class="text-xs text-slate-500 lowercase">{{ userProfile?.email || submissionData.email }}</p>
            </div>
            
            <!-- FIXED: Pulls DOB from Live Profile -->
            <div class="pt-4 border-t border-slate-50">
              <p class="text-[9px] font-black uppercase text-slate-400 mb-1">Date of Birth (Required for Check)</p>
              <p class="text-sm font-black text-[#0A1630]">
                {{ formatDate(userProfile?.dateOfBirth || submissionData.dateOfBirth, 'dateOnly') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 2. Evidence Vault -->
    <section v-if="allFiles.length > 0">
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <GlobeEuropeAfricaIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">2. Statutory Documentation</h3>
      </div>

      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 p-8 bg-slate-50 rounded-[40px] border border-slate-100 shadow-inner">
        <div v-for="(url, index) in allFiles" :key="index" class="group">
          <div v-if="isImage(url)" class="relative aspect-[3/4] overflow-hidden rounded-2xl border-4 border-white shadow-md transition-all group-hover:shadow-xl">
            <img :src="url" class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
            <a :href="url" target="_blank" class="absolute inset-0 bg-[#0A1630]/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
              <MagnifyingGlassIcon class="h-8 w-8 text-white" />
            </a>
          </div>
          
          <a v-else :href="url" target="_blank" class="flex flex-col items-center justify-center aspect-[3/4] p-4 bg-white border border-slate-200 rounded-2xl shadow-sm transition-all group-hover:border-[#20FCF1] group-hover:shadow-xl">
            <component :is="getFileIcon(url)" class="h-12 w-12 text-slate-300 group-hover:text-[#FD5E00] transition-colors mb-4" />
            <span class="text-[9px] font-black uppercase text-[#0A1630] tracking-tighter text-center line-clamp-2 px-2">{{ getFileNameFromUrl(url) }}</span>
          </a>
        </div>
      </div>
    </section>

    <!-- 3. Final Legal Sign-Off -->
    <section class="border-t pt-12">
      <div class="flex flex-col md:flex-row justify-between items-end gap-8">
        <div class="space-y-4">
          <div class="flex items-center gap-2">
            <ShieldCheckIcon class="h-5 w-5 text-[#20FCF1]" />
            <p class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Official Eligibility Seal</p>
          </div>
          <div class="p-6 bg-slate-900 rounded-[32px] border border-slate-800 max-w-sm">
            <p class="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Legal Declaration</p>
            <p class="text-xs text-slate-300 font-medium italic">"I formally confirm that the above information and documentation are true and correct to the best of my knowledge."</p>
          </div>
        </div>

        <div v-if="submissionData.signatureDataUrl || submissionData.signatureUrl" class="bg-white p-6 rounded-[32px] border border-slate-200 shadow-xl flex flex-col items-center min-w-[260px]">
          <p class="text-[9px] font-black uppercase text-slate-400 mb-4 tracking-widest">Authorized Signature</p>
          <img :src="submissionData.signatureDataUrl || submissionData.signatureUrl" class="max-h-20 object-contain mb-4" />
          <div class="w-full h-px bg-slate-100 mb-4"></div>
          <div class="flex flex-col items-center">
            <div class="flex items-center gap-2">
              <CheckBadgeIcon class="h-4 w-4 text-[#20FCF1]" />
              <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">Identity Attested</span>
            </div>
            <span class="text-[9px] font-bold text-slate-400 mt-2 uppercase">{{ formatDate(submissionData.submittedAt) }}</span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { 
  IdentificationIcon, GlobeEuropeAfricaIcon, ShieldCheckIcon, 
  CheckBadgeIcon, MagnifyingGlassIcon, ArrowTopRightOnSquareIcon,
  DocumentDuplicateIcon 
} from '@heroicons/vue/24/outline';

const props = defineProps({
  submissionData: { type: Object, required: true },
  userProfile: { type: Object, default: null }, // Added prop
  formatDate: { type: Function, required: true },
  formatText: { type: Function, required: true },
  getFileIcon: { type: Function, required: true },
  getFileNameFromUrl: { type: Function, required: true },
  isImage: { type: Function, required: true }
});

const copyFeedback = ref('');

const allFiles = computed(() => {
    const s = props.submissionData;
    const sources = [
        ...(Array.isArray(s.imageUrls) ? s.imageUrls : []),
        ...(Array.isArray(s.documentUrls) ? s.documentUrls : []),
        s.passportUrl, 
        s.visaUrl,
        s.documentUrl
    ].filter(url => typeof url === 'string' && url.length > 0);
    return [...new Set(sources)]; 
});

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    copyFeedback.value = 'Copied!';
    setTimeout(() => { copyFeedback.value = ''; }, 3000);
  } catch (err) {
    copyFeedback.value = 'Error';
  }
};
</script>

<style scoped>
.StatCard { @apply flex flex-col p-6 rounded-[28px] border border-slate-100 shadow-sm; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1; }
.StatCard .value { @apply text-sm font-black truncate; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

@media print {
  .no-print { display: none !important; }
}
</style>